/**
 * Process-key admission overlay helpers for the resource-profile dialog.
 * Помощники overlay admission (process key) для диалога профиля ресурсов.
 */

export interface ProcessCapCatalogItem {
  key: string;
  displayName?: string | null;
}

export interface ProcessCapRow {
  key: string;
  label: string;
  capInput: string;
}

export type ParsedCapInput =
  | { kind: 'inherit' }
  | { kind: 'value'; value: number }
  | { kind: 'invalid' };

export function parseCapInput(raw: string | null | undefined): ParsedCapInput {
  const trimmed = (raw ?? '').trim();
  if (trimmed.length === 0) {
    return { kind: 'inherit' };
  }
  if (!/^\d+$/.test(trimmed)) {
    return { kind: 'invalid' };
  }
  return { kind: 'value', value: Number(trimmed) };
}

export function processCapLabel(process: ProcessCapCatalogItem): string {
  const name = process.displayName?.trim();
  return name && name.length > 0 ? name : process.key;
}

export function buildProcessCapRows(
  processes: ProcessCapCatalogItem[] | null | undefined,
  overlay: Record<string, number> | null | undefined,
): ProcessCapRow[] {
  const overlayMap = overlay ?? {};
  const seen = new Set<string>();
  const rows: ProcessCapRow[] = [];

  const catalog = [...(processes ?? [])]
    .filter(process => !!process.key?.trim())
    .sort((left, right) => left.key.localeCompare(right.key));
  for (const process of catalog) {
    const key = process.key.trim();
    seen.add(key.toLowerCase());
    rows.push({
      key,
      label: processCapLabel(process),
      capInput: overlayValueText(overlayMap, key),
    });
  }

  const extraKeys = Object.keys(overlayMap)
    .filter(key => key.trim().length > 0 && !seen.has(key.trim().toLowerCase()))
    .sort((left, right) => left.localeCompare(right));
  for (const key of extraKeys) {
    const trimmed = key.trim();
    rows.push({
      key: trimmed,
      label: trimmed,
      capInput: overlayValueText(overlayMap, trimmed),
    });
  }

  return rows;
}

export function overlayFromCapRows(
  rows: ProcessCapRow[],
): { overlay: { [key: string]: number }; invalid: boolean } {
  const overlay: { [key: string]: number } = {};
  let invalid = false;
  for (const row of rows) {
    const parsed = parseCapInput(row.capInput);
    if (parsed.kind === 'invalid') {
      invalid = true;
      continue;
    }
    if (parsed.kind === 'value') {
      overlay[row.key] = parsed.value;
    }
  }
  return { overlay, invalid };
}

export function formatProcessConcurrencySummary(
  overlay: Record<string, number> | null | undefined,
): string {
  const keys = Object.keys(overlay ?? {}).filter(key => key.trim().length > 0).sort((left, right) => left.localeCompare(right));
  if (keys.length === 0) {
    return '';
  }
  return keys.map(key => `${key}: ${(overlay ?? {})[key]}`).join(', ');
}

function overlayValueText(overlay: Record<string, number>, key: string): string {
  const match = Object.entries(overlay).find(([overlayKey]) => overlayKey.toLowerCase() === key.toLowerCase());
  return match === undefined ? '' : String(match[1]);
}

export function overlayCapInput(
  overlay: Record<string, number> | null | undefined,
  processKey: string,
): string {
  return overlayValueText(overlay ?? {}, processKey);
}

export function applyCapToOverlay(
  overlay: Record<string, number> | null | undefined,
  processKey: string,
  capInput: string,
): { overlay: { [key: string]: number }; invalid: boolean } {
  const parsed = parseCapInput(capInput);
  const key = processKey.trim();
  const next: { [key: string]: number } = {};
  for (const [overlayKey, value] of Object.entries(overlay ?? {})) {
    if (overlayKey.trim().toLowerCase() === key.toLowerCase()) {
      continue;
    }
    next[overlayKey] = value;
  }
  if (parsed.kind === 'invalid') {
    return { overlay: next, invalid: true };
  }
  if (parsed.kind === 'value' && key.length > 0) {
    next[key] = parsed.value;
  }
  return { overlay: next, invalid: false };
}

export const SATELLITE_RESOURCE_WORKER_GROUP = 'satellite';

export function findActiveSatelliteProfile<T extends { workerGroup?: string | null; isActive?: boolean }>(
  profiles: T[] | null | undefined,
): T | null {
  return (profiles ?? []).find(profile =>
    profile.isActive === true
    && (profile.workerGroup ?? '').trim().toLowerCase() === SATELLITE_RESOURCE_WORKER_GROUP
  ) ?? null;
}
